import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CheckCircle2, Circle, Search } from 'lucide-react';
import FormControl from '../../../common/FormControl';

export interface DualListItem {
  id: string | number;
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  [key: string]: any;
}

interface DualListSelectorProps {
  availableItems: DualListItem[];
  selectedItems: DualListItem[];
  onSelectionChange: (selected: DualListItem[]) => void;
  titleLeft?: string;
  titleRight?: string;
  searchPlaceholder?: string;
  emptyMessageLeft?: string;
  emptyMessageRight?: string;
  /** When true the list is read-only: move controls and row selection are disabled. */
  disabled?: boolean;
}

const DualListSelector: React.FC<DualListSelectorProps> = ({
  availableItems,
  selectedItems,
  onSelectionChange,
  titleLeft = 'Available Items',
  titleRight = 'Selected Items',
  searchPlaceholder = 'Search…',
  emptyMessageLeft = 'No items available',
  emptyMessageRight = 'No items selected',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leftSelection, setLeftSelection] = useState<Set<string | number>>(new Set());
  const [rightSelection, setRightSelection] = useState<Set<string | number>>(new Set());

  const availableList = useMemo(() => {
    return availableItems.filter(
      item =>
        !selectedItems.find(s => s.id === item.id) &&
        (item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category?.toLowerCase().includes(searchTerm.toLowerCase())),
    );
  }, [availableItems, selectedItems, searchTerm]);

  const handleMoveRight    = () => { onSelectionChange([...selectedItems, ...availableList.filter(i => leftSelection.has(i.id))]); setLeftSelection(new Set()); };
  const handleMoveAllRight = () => { onSelectionChange([...selectedItems, ...availableList]); setLeftSelection(new Set()); };
  const handleMoveLeft     = () => { onSelectionChange(selectedItems.filter(i => !rightSelection.has(i.id))); setRightSelection(new Set()); };
  const handleMoveAllLeft  = () => { onSelectionChange([]); setRightSelection(new Set()); };

  const toggleLeft  = (id: string | number) => { const s = new Set(leftSelection);  s.has(id) ? s.delete(id) : s.add(id); setLeftSelection(s); };
  const toggleRight = (id: string | number) => { const s = new Set(rightSelection); s.has(id) ? s.delete(id) : s.add(id); setRightSelection(s); };

  const renderItem = (item: DualListItem, isSelected: boolean, onClick: () => void) => (
    <div
      key={item.id}
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '9px 12px',
        borderBottom: '1px solid var(--border-light)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: isSelected ? 'var(--brand-subtle)' : 'transparent',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!disabled && !isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface-page)'; }}
      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
    >
      <span style={{ color: isSelected ? 'var(--brand)' : 'var(--gray-300)', marginTop: 1, flexShrink: 0 }}>
        {isSelected ? <CheckCircle2 size={16} /> : <Circle size={16} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title}
        </div>
        {item.subtitle && (
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{item.subtitle}</div>
        )}
      </div>
    </div>
  );

  const ctrlBtn = (onClick: () => void, disabled: boolean, icon: React.ReactNode, title: string) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 6,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-strong)',
        color: disabled ? 'var(--text-muted)' : 'var(--brand)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'border-color 0.12s, background 0.12s',
      }}
    >
      {icon}
    </button>
  );

  return (
    <div style={{ display: 'flex', height: '100%', gap: 10 }}>

      {/* ── Left panel ── */}
      <div className="netbody-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-strong)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
            {titleLeft} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({availableList.length})</span>
          </div>
          <FormControl.Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={13} />}
            size="sm"
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {availableList.length > 0
            ? availableList.map(item => renderItem(item, leftSelection.has(item.id), () => toggleLeft(item.id)))
            : <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>{emptyMessageLeft}</div>
          }
        </div>
      </div>

      {/* ── Controls ── */}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
        {ctrlBtn(handleMoveAllRight, disabled || availableList.length === 0,   <ChevronsRight size={16} />, 'Add All')}
        {ctrlBtn(handleMoveRight,    disabled || leftSelection.size === 0,     <ChevronRight  size={16} />, 'Add Selected')}
        {ctrlBtn(handleMoveLeft,     disabled || rightSelection.size === 0,    <ChevronLeft   size={16} />, 'Remove Selected')}
        {ctrlBtn(handleMoveAllLeft,  disabled || selectedItems.length === 0,   <ChevronsLeft  size={16} />, 'Remove All')}
      </div>

      {/* ── Right panel ── */}
      <div className="netbody-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-strong)', display: 'flex', alignItems: 'center', minHeight: 60 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
            {titleRight} <span style={{ color: 'var(--brand)', fontWeight: 700 }}>({selectedItems.length})</span>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {selectedItems.length > 0
            ? selectedItems.map(item => renderItem(item, rightSelection.has(item.id), () => toggleRight(item.id)))
            : <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic' }}>{emptyMessageRight}</div>
          }
        </div>
      </div>

    </div>
  );
};

export default DualListSelector;
